import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Search, Upload, Trash2, Link2, Download, FileText, Scale } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Card } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import EtifoodDdtImportDialog from "../../components/EtifoodDdtImportDialog";
import EtifoodQuadraturaDialog from "../../components/EtifoodQuadraturaDialog";
import { useTableSort, SortableTh } from "../../components/tableSort";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmtNum = (n) => (n ?? 0).toLocaleString("it-IT", { maximumFractionDigits: 2 });
const fmtEur = (n) => (n ?? 0).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtDate = (d) => (d ? d.split("-").reverse().join("/") : "—");

const LinkCustomerDialog = ({ ddt, onClose, onLinked }) => {
  const [search, setSearch] = useState("");
  const [customers, setCustomers] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => { axios.get(`${API}/customers`).then((r) => setCustomers(r.data || [])).catch(() => {}); }, []);
  const filtered = customers.filter((c) => (c.business_name || "").toLowerCase().includes(search.toLowerCase())).slice(0, 20);

  const link = async (customerId) => {
    setSaving(true);
    try {
      const r = await axios.post(`${API}/etifood-sales/ddt/${ddt.id}/link-customer`, { customer_id: customerId, apply_to_all: true });
      toast.success(`Collegato a ${r.data.customer_name}${r.data.agent_name ? ` (agente ${r.data.agent_name})` : ""} — ${r.data.updated_docs} DDT aggiornati`);
      onLinked(); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>Collega "{ddt.customer_name}" a un cliente dell'app</DialogTitle></DialogHeader>
        <p className="text-xs text-stone-500">Il collegamento verrà applicato a tutti i DDT di questo cliente e memorizzato per gli import futuri.</p>
        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Cerca cliente…" data-testid="link-customer-search" />
        <div className="max-h-60 overflow-auto border border-stone-200 rounded-lg">
          {filtered.map((c) => (
            <button key={c.id} disabled={saving} onClick={() => link(c.id)} className="w-full text-left px-3 py-2 text-sm hover:bg-stone-50 border-b border-stone-100 last:border-0" data-testid={`link-customer-opt-${c.id}`}>
              {c.business_name}
            </button>
          ))}
          {filtered.length === 0 && <p className="p-3 text-sm text-stone-500">Nessun cliente trovato</p>}
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Annulla</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const DdtDetailDialog = ({ ddtId, onClose }) => {
  const [doc, setDoc] = useState(null);
  useEffect(() => {
    axios.get(`${API}/etifood-sales/ddt/${ddtId}`).then((r) => setDoc(r.data)).catch(() => toast.error("Errore caricamento DDT"));
  }, [ddtId]);
  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto">
        <DialogHeader><DialogTitle>DDT n. {doc?.number} del {fmtDate(doc?.date)} — {doc?.customer_name}</DialogTitle></DialogHeader>
        {!doc ? <p className="p-4 text-sm text-stone-500">Caricamento…</p> : (
          <div className="space-y-3">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
              <div><p className="text-xs text-stone-500">Agente</p><p className="font-semibold">{doc.agent_name || "—"}</p></div>
              <div><p className="text-xs text-stone-500">Pagamento</p><p className="font-semibold">{doc.payment_terms || "—"}</p></div>
              <div><p className="text-xs text-stone-500">Totale Kg</p><p className="font-semibold">{fmtNum(doc.total_kg)}</p></div>
              <div><p className="text-xs text-stone-500">Netto righe €</p><p className="font-semibold">{fmtEur(doc.lines_net)}</p></div>
            </div>
            <div className="border border-stone-200 rounded-lg overflow-hidden">
              <div className="overflow-x-auto max-h-[55vh] overflow-y-auto">
                <table className="w-full text-sm min-w-[760px]">
                  <thead className="bg-stone-50 sticky top-0">
                    <tr>
                      <th className="text-left p-2 text-xs font-semibold text-stone-600">Codice</th>
                      <th className="text-left p-2 text-xs font-semibold text-stone-600">Articolo</th>
                      <th className="text-left p-2 text-xs font-semibold text-stone-600">Lotto</th>
                      <th className="text-left p-2 text-xs font-semibold text-stone-600">Barcode</th>
                      <th className="text-right p-2 text-xs font-semibold text-stone-600">Colli</th>
                      <th className="text-right p-2 text-xs font-semibold text-stone-600">Kg</th>
                      <th className="text-right p-2 text-xs font-semibold text-stone-600">€/Kg</th>
                      <th className="text-right p-2 text-xs font-semibold text-stone-600">Sc.%</th>
                      <th className="text-right p-2 text-xs font-semibold text-stone-600">Netto €</th>
                    </tr>
                  </thead>
                  <tbody>
                    {doc.lines.map((l, i) => (
                      <tr key={i} className="border-t border-stone-100" data-testid={`ddt-line-${i}`}>
                        <td className="p-2 font-mono text-xs">{l.product_code}</td>
                        <td className="p-2 truncate max-w-[260px]">{l.description_clean}{l.is_gift && <Badge className="ml-1 bg-emerald-100 text-emerald-800 hover:bg-emerald-100 text-[9px]">OMAGGIO</Badge>}</td>
                        <td className="p-2 font-mono text-xs text-[#8A1C2A]">{l.lot_info || "—"}</td>
                        <td className="p-2 font-mono text-xs text-stone-500">{l.barcode || "—"}</td>
                        <td className="p-2 text-right">{fmtNum(l.pieces)}</td>
                        <td className="p-2 text-right font-semibold">{fmtNum(l.quantity_kg)}</td>
                        <td className="p-2 text-right">{fmtEur(l.unit_price)}</td>
                        <td className="p-2 text-right">{l.discount_pct > 0 ? fmtNum(l.discount_pct) : "—"}</td>
                        <td className="p-2 text-right font-semibold">{l.is_gift ? "0,00" : fmtEur(l.net_amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
        <DialogFooter><Button variant="outline" onClick={onClose} data-testid="ddt-detail-close">Chiudi</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const DdtArchive = ({ reloadKey, onImported }) => {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [unlinkedOnly, setUnlinkedOnly] = useState(false);
  const [detailId, setDetailId] = useState(null);
  const [linkDdt, setLinkDdt] = useState(null);
  const sort = useTableSort();

  const load = useCallback(async () => {
    try {
      const params = {};
      if (search) params.search = search;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      if (unlinkedOnly) params.unlinked_only = true;
      const r = await axios.get(`${API}/etifood-sales/ddt`, { params });
      setDocs(r.data || []);
    } catch { toast.error("Errore caricamento DDT"); }
    finally { setLoading(false); }
  }, [search, dateFrom, dateTo, unlinkedOnly]);

  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [load, reloadKey]);

  const deleteDdt = async (d) => {
    if (!window.confirm(`Eliminare il DDT n. ${d.number} del ${fmtDate(d.date)}?`)) return;
    try { await axios.delete(`${API}/etifood-sales/ddt/${d.id}`); toast.success("DDT eliminato"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };

  const unlinkedCount = docs.filter((d) => !d.customer_id).length;

  return (
    <div className="space-y-3">
      <div className="flex flex-col lg:flex-row gap-2">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Cerca n. DDT, cliente, articolo…" className="pl-9" data-testid="ddt-search-input" />
        </div>
        <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="w-full lg:w-40" data-testid="ddt-date-from" />
        <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="w-full lg:w-40" data-testid="ddt-date-to" />
        <label className="flex items-center gap-2 text-sm text-stone-600 whitespace-nowrap px-2">
          <input type="checkbox" checked={unlinkedOnly} onChange={(e) => setUnlinkedOnly(e.target.checked)} className="accent-[#8A1C2A]" data-testid="ddt-unlinked-filter" />
          Solo non collegati {unlinkedCount > 0 && <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100">{unlinkedCount}</Badge>}
        </label>
      </div>

      <Card className="bg-white border-stone-200 overflow-hidden">
        <div className="overflow-x-auto max-h-[62vh] overflow-y-auto">
          <table className="w-full text-sm min-w-[860px]">
            <thead className="bg-stone-50 border-b border-stone-200 sticky top-0 z-10">
              <tr>
                <SortableTh label="N." sortId="number" sort={sort} />
                <SortableTh label="Data" sortId="date" sort={sort} />
                <SortableTh label="Cliente" sortId="customer_name" sort={sort} />
                <SortableTh label="Agente" sortId="agent_name" sort={sort} />
                <SortableTh label="Righe" sortId="lines_count" sort={sort} align="right" />
                <SortableTh label="Kg" sortId="total_kg" sort={sort} align="right" />
                <SortableTh label="Netto €" sortId="lines_net" sort={sort} align="right" />
                <th className="p-3"></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="8" className="p-8 text-center text-stone-500">Caricamento…</td></tr>
              ) : docs.length === 0 ? (
                <tr><td colSpan="8" className="p-8 text-center text-stone-500">Nessun DDT — carica l'export «DDT DETTAGLIO» da Etifood</td></tr>
              ) : sort.sortList(docs).map((d) => (
                <tr key={d.id} className="border-b border-stone-100 hover:bg-stone-50 cursor-pointer" onClick={() => setDetailId(d.id)} data-testid={`ddt-row-${d.number}`}>
                  <td className="p-3 font-bold text-[#8A1C2A]">{d.number}</td>
                  <td className="p-3">{fmtDate(d.date)}</td>
                  <td className="p-3 font-medium truncate max-w-[220px]">
                    {d.customer_name}
                    {!d.customer_id && <Badge className="ml-1.5 bg-amber-100 text-amber-800 hover:bg-amber-100 text-[9px]">NON COLLEGATO</Badge>}
                  </td>
                  <td className="p-3 text-stone-600">{d.agent_name || "—"}</td>
                  <td className="p-3 text-right">{d.lines_count}</td>
                  <td className="p-3 text-right">{fmtNum(d.total_kg)}</td>
                  <td className="p-3 text-right font-semibold">{fmtEur(d.lines_net)}</td>
                  <td className="p-3 text-right" onClick={(e) => e.stopPropagation()}>
                    <div className="flex justify-end gap-2">
                      {!d.customer_id && (
                        <button onClick={() => setLinkDdt(d)} className="text-amber-500 hover:text-[#8A1C2A]" title="Collega cliente" data-testid={`ddt-link-${d.number}`}>
                          <Link2 className="w-4 h-4" />
                        </button>
                      )}
                      <button onClick={() => deleteDdt(d)} className="text-stone-400 hover:text-red-600" title="Elimina" data-testid={`ddt-delete-${d.number}`}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {detailId && <DdtDetailDialog ddtId={detailId} onClose={() => setDetailId(null)} />}
      {linkDdt && <LinkCustomerDialog ddt={linkDdt} onClose={() => setLinkDdt(null)} onLinked={() => { load(); onImported?.(); }} />}
    </div>
  );
};

export default function EtifoodSales() {
  const [importOpen, setImportOpen] = useState(false);
  const [quadOpen, setQuadOpen] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  return (
    <div className="space-y-4" data-testid="etifood-sales-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-stone-900 flex items-center gap-2"><FileText className="w-6 h-6 text-[#8A1C2A]" />Import DDT Etifood</h1>
          <p className="text-sm text-stone-500">Caricamento file "DDT DETTAGLIO", archivio documenti e quadratura. Le statistiche sono nella Dashboard BI.</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" onClick={() => setQuadOpen(true)} data-testid="quadratura-btn" className="h-11 border-[#8A1C2A]/40 text-[#8A1C2A] hover:bg-[#8A1C2A]/5"><Scale className="w-4 h-4 mr-1" />Verifica quadratura</Button>
          <Button onClick={() => setImportOpen(true)} className="h-11 bg-[#8A1C2A] hover:bg-[#701622]" data-testid="import-ddt-btn">
            <Upload className="w-4 h-4 mr-1" />Importa DDT
          </Button>
        </div>
      </div>

      <DdtArchive reloadKey={reloadKey} onImported={() => setReloadKey((k) => k + 1)} />

      <EtifoodDdtImportDialog open={importOpen} onClose={() => setImportOpen(false)} onImported={() => setReloadKey((k) => k + 1)} />
      <EtifoodQuadraturaDialog open={quadOpen} onClose={() => setQuadOpen(false)} />
    </div>
  );
}
